import type { ProductPageProps } from '../components/pages/product-details/client';
import { env } from '../lib';

interface KlaviyoProduct {
  ProductID: string;
  ProductName: string;
  ProductCategories?: string[];
  ProductURL: string;
  ImageURL?: string;
  Price: number;
  CompareAtPrice?: number;
}

export interface KlaviyoCartItem extends KlaviyoProduct {
  Quantity: number;
}

interface ViewedProductEvent {
  event: 'Viewed Product';
  customer_properties?: Record<string, unknown>;
  properties: KlaviyoProduct;
}

interface ViewedCategoryEvent {
  event: 'Viewed Category';
  customer_properties?: Record<string, unknown>;
  properties: {
    CategoryName: string;
    CategoryID: string;
    ImageURL?: string;
    URL: string;
  };
}

interface SearchedSiteEvent {
  event: 'Searched Site';
  customer_properties?: Record<string, unknown>;
  properties: {
    SearchTerm: string;
    'SearchTerm (autocorrected)'?: string;
    ReturnedResults: number;
  };
}

interface ClickedBannerEvent {
  event: 'Clicked Banner';
  customer_properties?: Record<string, unknown>;
  properties: {
    SourceURL: string;
    DestinationURL: string;
    BannerTitle: string;
  };
}

interface AddedToCartEvent {
  event: 'Added to Cart';
  customer_properties?: Record<string, unknown>;
  properties: {
    $value: number;
    AddedItemCategories?: string[];
    Items: KlaviyoCartItem[];
  };
}

interface AddedSampleToCartEvent {
  event: 'Added Sample to Cart';
  customer_properties?: Record<string, unknown>;
  properties: {
    $value: number;
    AddedItemCategories?: string[];
    Items: KlaviyoCartItem[];
  };
}

interface StartedCheckoutEvent {
  event: 'Started Checkout';
  customer_properties?: Record<string, unknown>;
  properties: {
    $value: number;
    Items: KlaviyoCartItem[];
  };
}

type KlaviyoEvent =
  | ViewedProductEvent
  | ViewedCategoryEvent
  | SearchedSiteEvent
  | ClickedBannerEvent
  | AddedToCartEvent
  | AddedSampleToCartEvent
  | StartedCheckoutEvent;

const trackKlaviyoEvent = (event: KlaviyoEvent) => {
  if (typeof window === 'undefined' || !window._learnq) return;

  window._learnq.push([
    'track',
    event.event,
    event.properties,
    event.customer_properties,
  ]);
};

interface IdentifyProps {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  externalId?: string;
  [key: string]: unknown;
}

export const identify = ({ email, ...properties }: IdentifyProps) => {
  if (typeof window === 'undefined' || !window._learnq) return;

  window._learnq.push([
    'identify',
    {
      $email: email,
      ...properties,
    },
  ]);
};

export const trackViewedProductKlaviyo = ({
  product,
  customerProps,
}: {
  product: ProductPageProps;
  customerProps?: Record<string, unknown>;
}) => {
  const klaviyoProduct = convertSanityProductToKlaviyoProduct(product);

  trackKlaviyoEvent({
    event: 'Viewed Product',
    properties: klaviyoProduct,
    customer_properties: customerProps,
  });
};

export const trackViewedCategoryKlaviyo = ({
  category,
  customerProps,
}: {
  category: {
    CategoryName: string;
    CategoryID: string;
    ImageURL?: string;
    URL: string;
  };
  customerProps?: Record<string, unknown>;
}) => {
  trackKlaviyoEvent({
    event: 'Viewed Category',
    properties: category,
    customer_properties: customerProps,
  });
};

export const trackSearchedSiteKlaviyo = ({
  searchTerm,
  autocorrectedTerm,
  resultCount,
  customerProps,
}: {
  searchTerm: string;
  autocorrectedTerm?: string;
  resultCount: number;
  customerProps?: Record<string, unknown>;
}) => {
  trackKlaviyoEvent({
    event: 'Searched Site',
    properties: {
      SearchTerm: searchTerm,
      'SearchTerm (autocorrected)': autocorrectedTerm,
      ReturnedResults: resultCount,
    },
    customer_properties: customerProps,
  });
};

export const trackClickedBannerKlaviyo = ({
  sourceUrl,
  destinationUrl,
  title,
  customerProps,
}: {
  sourceUrl: string;
  destinationUrl: string;
  title: string;
  customerProps?: Record<string, unknown>;
}) => {
  trackKlaviyoEvent({
    event: 'Clicked Banner',
    properties: {
      SourceURL: sourceUrl,
      DestinationURL: destinationUrl,
      BannerTitle: title,
    },
    customer_properties: customerProps,
  });
};

export const trackAddedToCartKlaviyo = ({
  items,
  totalValue,
  categories,
  customerProps,
}: {
  items: KlaviyoCartItem[];
  totalValue: number;
  categories?: string[];
  customerProps?: Record<string, unknown>;
}) => {
  trackKlaviyoEvent({
    event: 'Added to Cart',
    properties: {
      $value: totalValue,
      AddedItemCategories: categories,
      Items: items,
    },
    customer_properties: customerProps,
  });
};

export const trackAddedSampleToCartKlaviyo = ({
  items,
  totalValue,
  categories,
  customerProps,
}: {
  items: KlaviyoCartItem[];
  totalValue: number;
  categories?: string[];
  customerProps?: Record<string, unknown>;
}) => {
  trackKlaviyoEvent({
    event: 'Added Sample to Cart',
    properties: {
      $value: totalValue,
      AddedItemCategories: categories,
      Items: items,
    },
    customer_properties: customerProps,
  });
};

export const trackStartedCheckoutKlaviyo = ({
  items,
  totalValue,
  customerProps,
}: {
  items: KlaviyoCartItem[];
  totalValue: number;
  customerProps?: Record<string, unknown>;
}) => {
  trackKlaviyoEvent({
    event: 'Started Checkout',
    properties: {
      $value: totalValue,
      Items: items,
    },
    customer_properties: customerProps,
  });
};

function convertSanityProductToKlaviyoProduct(
  product: ProductPageProps
): KlaviyoProduct {
  return {
    ProductID: product.productGid ?? '',
    ProductName: product.title,
    ProductURL: product.slug ? `/products/${product.slug}` : '',
    Price: product.variants?.[0]?.price ?? 0,
    CompareAtPrice: product.variants?.[0]?.compareAtPrice ?? 0,
    ProductCategories: product.category ? [product.category] : [],
    // ImageURL: product.mainImage ? urlForImage(product.mainImage).url() : '',
    // TODO: add image url
  };
}

export function hasKlaviyo() {
  return env.NEXT_PUBLIC_KLAVIYO_PUBLIC_KEY !== undefined;
}

// Type declaration for Klaviyo's _learnq
declare global {
  interface Window {
    _learnq: {
      push: (args: unknown[]) => void;
    };
  }
}
